import { useQuery } from "@tanstack/react-query";
import type { WidgetField, WidgetFieldSource } from "@/domains/buildings/types/buildingWidget";
import { sameAttributeValue } from "@/utils/attributeValue";
import {
  searchDocumentContent,
  type ContentMatchInfo,
} from "@/stores/documents/repository/queries";

type MachineSource = Exclude<WidgetFieldSource, { kind: "user" }>;

export interface FieldEvidence {
  source: MachineSource | null;
  /** A document filename, or an EHR registry name — see `canPreview`. */
  filename: string | null;
  /**
   * True only when `filename` names a document the preview can resolve. An EHR
   * source reports its registry ("EHR") here, which `getDocumentByFilename`
   * cannot find, so consumers must not offer "Open document" for it.
   */
  canPreview: boolean;
  pageNumber: number | null;
  sheetName: string | null;
  passage: string | null;
  mentionCount: number;
}

/**
 * Whether `EvidenceSourceCard` will have anything to draw — the guard the
 * inspector and the review dialog apply before rendering their SOURCE EVIDENCE
 * heading. Answered from the field, never from the settled query: a guard that
 * waits for the result mounts the section for the length of the fetch and then
 * removes it, heading and all, shifting whatever follows back up.
 *
 * The field is enough, because the evidence is built from the first matching
 * source and every source that survives that filter draws something — a
 * registry chip for EHR (the chip *is* the provenance; the one registry we read
 * reports an empty `api_building_data.source_system`, so its `filename` is null
 * and there is frequently no reasoning either), a filename for a document. No
 * matching source is the only way to end up with nothing.
 */
export function fieldHasSourceEvidence(field: WidgetField | null, selectedValue: string): boolean {
  return evidenceSources(field, selectedValue).length > 0;
}

export interface PageLocation {
  pageNumber: number | null;
  sheetName: string | null;
}

/**
 * Only sources that actually reported the selected value. A near-miss is not
 * evidence: falling back to the other sources would render another document's
 * filename, page and passage as the provenance of a value it never stated —
 * the same invented provenance `pageLocationForSource` refuses. An empty list
 * leaves the callers on their no-evidence state, which is what `FieldRow`
 * already shows for the same field.
 *
 * Exported for unit testing.
 */
export function matchingMachineSources(field: WidgetField, selectedValue: string): MachineSource[] {
  const sources = field.sources.filter((source): source is MachineSource => source.kind !== "user");
  if (!selectedValue) return sources;
  return sources.filter((source) => sameAttributeValue(source.value, selectedValue));
}

export function pageNumberFromReasoning(reasoning: string | null): number | null {
  const match = reasoning?.match(/\b(?:page|pg|p\.)\s*[:#-]?\s*(\d+)/i);
  return match ? Number(match[1]) : null;
}

/** Keep the selected evidence compact while retaining complete words around the match. */
export function evidenceExcerpt(pageText: string, matchedText: string, radius = 260): string {
  const compact = pageText.replace(/\s+/g, " ").trim();
  if (!compact) return "";
  const index = matchedText ? compact.toLowerCase().indexOf(matchedText.toLowerCase()) : -1;
  if (index < 0) return compact.slice(0, radius * 2).trim();
  const start = Math.max(0, index - radius);
  const end = Math.min(compact.length, index + matchedText.length + radius);
  const clipped = compact.slice(start, end).trim();
  return `${start > 0 ? "…" : ""}${clipped}${end < compact.length ? "…" : ""}`;
}

function baseEvidence(source: MachineSource | null, mentionCount: number): FieldEvidence {
  return {
    source,
    filename: null,
    canPreview: false,
    pageNumber: null,
    sheetName: null,
    passage: null,
    mentionCount,
  };
}

function ehrEvidence(
  source: Extract<MachineSource, { kind: "ehr" }>,
  mentionCount: number
): FieldEvidence {
  return {
    source,
    filename: source.sourceSystem || null,
    canPreview: false,
    pageNumber: pageNumberFromReasoning(source.reasoning),
    sheetName: null,
    passage: source.reasoning,
    mentionCount,
  };
}

/**
 * Only the source's own document can locate its evidence. Another document's
 * match would be rendered as a page of `source.filename`, i.e. invented
 * provenance — fall back to the page named in the reasoning instead.
 *
 * Exported for unit testing.
 */
export function pageLocationForSource(
  matches: Map<number, ContentMatchInfo>,
  documentId: number | null,
  fallbackPage: number | null
): PageLocation {
  const match = documentId === null ? undefined : matches.get(documentId);
  return {
    pageNumber: match?.firstMatchPage ?? fallbackPage,
    sheetName: match?.sheetName ?? null,
  };
}

async function resolvePageLocation(
  buildingId: string,
  selectedValue: string,
  documentId: number | null,
  fallbackPage: number | null
): Promise<PageLocation> {
  const fallback = { pageNumber: fallbackPage, sheetName: null };
  if (!selectedValue || documentId === null) return fallback;
  const matches = await searchDocumentContent(buildingId, selectedValue);
  return pageLocationForSource(matches, documentId, fallbackPage);
}

async function loadFieldEvidence(
  buildingId: string | undefined,
  field: WidgetField | null,
  selectedValue: string,
  primary: MachineSource | null,
  sources: MachineSource[]
): Promise<FieldEvidence> {
  if (!buildingId || !field || !primary) return baseEvidence(primary, sources.length);
  if (primary.kind === "ehr") return ehrEvidence(primary, sources.length);
  const location = await resolvePageLocation(
    buildingId,
    selectedValue,
    primary.documentId,
    pageNumberFromReasoning(primary.reasoning)
  );
  return {
    source: primary,
    filename: primary.filename,
    canPreview: true,
    pageNumber: location.pageNumber,
    sheetName: location.sheetName,
    passage: primary.reasoning,
    mentionCount: sources.length,
  };
}

/**
 * Resolves the first source page containing the selected value through the
 * established RLS-scoped content-search RPC. Evidence text comes from the
 * machine finding's own reasoning; the browser never reads the unscoped raw OCR
 * tables directly.
 */
export function useFieldEvidence(
  buildingId: string | undefined,
  field: WidgetField | null,
  selectedValue: string,
  enabled: boolean
) {
  const sources = evidenceSources(field, selectedValue);
  const primary = sources[0] ?? null;
  const documentId = primary && primary.kind !== "ehr" ? primary.documentId : null;

  return useQuery<FieldEvidence>({
    queryKey: ["building-field-evidence", buildingId, field?.id, selectedValue, documentId],
    enabled: enabled && !!buildingId && !!field,
    queryFn: () => loadFieldEvidence(buildingId, field, selectedValue, primary, sources),
    staleTime: 5 * 60 * 1000,
  });
}

function evidenceSources(field: WidgetField | null, selectedValue: string): MachineSource[] {
  if (!field || field.state === "empty") return [];
  return matchingMachineSources(field, selectedValue);
}
